import classNames from 'classnames'
import { ChevronDown } from 'lucide-react'
import { observer } from 'mobx-react-lite'
import React from 'react'
import { useLocation } from 'react-router-dom'
import { PreservingLink } from '../ui/PreservingLink'
import { isItemActive, NavSection as NavSectionT } from './items'
import { navV2Store } from './store'

interface Props {
    section: NavSectionT
}

// Renders one section. Every section with a label is collapsible — click the
// header to toggle. State is persisted to localStorage.
// Sections without a label (e.g. "History") render their items directly with
// no header and no toggle.
export const NavSection = observer(({ section }: Props) => {
    const { pathname } = useLocation()
    const hasHeader = section.label !== null
    const collapsed = hasHeader && navV2Store.isCollapsed(section.key)

    return (
        <div className="mt-2 first:mt-0">
            {hasHeader && (
                <SectionHeader
                    label={section.label as string}
                    collapsed={collapsed}
                    onToggle={() => navV2Store.toggleSection(section.key)}
                />
            )}
            {!collapsed && (
                <ul
                    className={classNames(
                        'relative mt-0.5 ml-3 pl-1',
                        'before:absolute before:left-0 before:top-1 before:bottom-1',
                        'before:w-px before:bg-mjGray-600',
                    )}
                >
                    {section.items.map((item) => {
                        const active = isItemActive(item, pathname)
                        return (
                            <li key={item.to}>
                                <PreservingLink
                                    to={item.to}
                                    className={classNames(
                                        'flex items-center mx-0.5 px-2.5 py-1.5 rounded-md',
                                        'text-[14px] leading-5 transition-colors duration-75',
                                        'no-underline hover:no-underline',
                                        'focus:outline-none focus-visible:outline-none focus-visible:ring-0',
                                        active
                                            ? 'bg-mjGray-400 text-mjDark-1000 font-medium'
                                            : 'text-mjDark-800 hover:bg-mjGray-300',
                                    )}
                                >
                                    {item.label}
                                </PreservingLink>
                            </li>
                        )
                    })}
                </ul>
            )}
        </div>
    )
})

interface SectionHeaderProps {
    label: string
    collapsed: boolean
    onToggle: () => void
}

// Muted sentence-case section heading with a leading chevron. Whole row is
// clickable. Chevron rotates from down (expanded) to right (collapsed).
const SectionHeader = ({ label, collapsed, onToggle }: SectionHeaderProps) => {
    return (
        <button
            type="button"
            onClick={onToggle}
            aria-expanded={!collapsed}
            className={classNames(
                'w-[calc(100%-0.75rem)] h-8 mx-1.5 mt-3 flex items-center gap-1.5 px-1.5 rounded-md',
                'text-left text-[12px] font-medium text-mjDark-600',
                'transition-colors duration-75 hover:bg-mjGray-300 hover:text-mjDark-1000',
                'focus:outline-none',
            )}
        >
            <ChevronDown
                size={14}
                className={classNames(
                    'shrink-0 transition-transform duration-75',
                    collapsed ? '-rotate-90' : 'rotate-0',
                )}
            />
            <span>{label}</span>
        </button>
    )
}
